import { ElementTag } from '@probolabs/probo-shared';
import { findDropdowns, findClickables, findToggles, findNonInteractiveElements, findCheckables, findFillables } from './finders';
import { uniquifyElements, highlighterLogger, getAllFrames } from './utils';
import { getElementInfo, findElementByInfo, findElementBySelectors } from './smart-selectors';
import { findMatchingCandidateElementInfo } from './element-matching';

export const highlight = {
  execute: async function(elementTypes, handleScroll=false) {
    const elements = await findElements(elementTypes);
    highlightElements(elements, false,handleScroll);
    return elements;
  },

  unexecute: function(handleScroll=false) {
    unhighlightElements(handleScroll)
  },

  generateJSON: async function() {
    const json = {};

   // Capture viewport dimensions
   const viewportData = {
    width: window.innerWidth,
    height: window.innerHeight,
    documentWidth: document.documentElement.clientWidth,
    documentHeight: document.documentElement.clientHeight,
    timestamp: new Date().toISOString()
  };

    // Add viewport data to the JSON output
    json.viewport = viewportData;


    await Promise.all(Object.values(ElementTag).map(async elementType => {
      const elements = await findElements(elementType);
      json[elementType] = elements;
    }));

    // Serialize the JSON object
    const jsonString = JSON.stringify(json, null, 4); // Pretty print with 4 spaces

    highlighterLogger.log(`JSON: ${jsonString}`);
    return jsonString;
  },

  getElementInfo
};


export function unhighlightElements(handleScroll=false) {
  const documents = getAllFrames();
  documents.forEach(doc => {
    const overlay = doc.getElementById('highlight-overlay');
    if (overlay) {
      if (handleScroll) {
        // Remove event listeners
        doc.removeEventListener('scroll', overlay.scrollHandler, true);
        doc.removeEventListener('resize', overlay.resizeHandler);
      }
      overlay.remove();
    }
  })
}


export async function findElements(elementTypes, verbose=true) {
  const typesArray = Array.isArray(elementTypes) ? elementTypes : [elementTypes];
  highlighterLogger.debug('Starting element search for types:', typesArray);

  const elements = [];
  typesArray.forEach(elementType => {
    if (elementType === ElementTag.FILLABLE) {
      elements.push(...findFillables());
    }
    if (elementType === ElementTag.SELECTABLE) {
      elements.push(...findDropdowns());
    }
    if (elementType === ElementTag.CLICKABLE) {
      elements.push(...findClickables());
      elements.push(...findToggles());
      elements.push(...findCheckables());
    }
    if (elementType === ElementTag.NON_INTERACTIVE_ELEMENT) {
      elements.push(...findNonInteractiveElements());
    }
  });

  // console.log('Before uniquify:', elements.length);
  const elementsWithInfo = elements.map((element, index) => 
    getElementInfo(element, index)
  );

  
  
  const uniqueElements = uniquifyElements(elementsWithInfo);
  highlighterLogger.debug(`Found ${uniqueElements.length} elements:`);
  
  // More comprehensive visibility check
  const visibleElements = uniqueElements.filter(elementInfo => {
    const el = elementInfo.element;
    const style = getComputedStyle(el);
    
    // Check various style properties that affect visibility
    if (style.display === 'none' || 
        style.visibility === 'hidden') {
      return false;
    }
    
    // Check if element has non-zero dimensions
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }
    
    // Check if element is within viewport
    if (rect.bottom < 0 || 
        rect.top > window.innerHeight || 
        rect.right < 0 || 
        rect.left > window.innerWidth) {
      // Element is outside viewport, but still might be valid 
      // if user scrolls to it, so we'll include it
      return true;
    }
    
    return true;
  });
  
  highlighterLogger.debug(`Out of which ${visibleElements.length} elements are visible:`);
  if (verbose) {
    visibleElements.forEach(info => {
      highlighterLogger.debug(`Element ${info.index}:`, info);
    });
  }
  
  return visibleElements;
}

// elements is an array of objects with index, xpath
export function highlightElements(elements, enableSmartSelectors=false, handleScroll=false) {
  // console.log('[highlightElements] called with', elements.length, 'elements');
  // Create overlay if it doesn't exist and store it in a dictionary
  const documents = getAllFrames();  
  let overlays = {};
  documents.forEach(doc => {
    let overlay = doc.getElementById('highlight-overlay');
    if (!overlay) {
      overlay = doc.createElement('div');
      overlay.id = 'highlight-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 2147483647;
      `;
      doc.body.appendChild(overlay);
      // console.log('[highlightElements] Created overlay in document:', doc);
    }
    overlays[doc.documentURI] = overlay;
  });
  

  const updateHighlights = (doc = null) => {
    if (doc) {
      overlays[doc.documentURI].innerHTML = '';
    } else {
      Object.values(overlays).forEach(overlay => { overlay.innerHTML = '' });
    };
    
    elements.forEach((elementInfo, idx) => {
      //console.log(`[highlightElements] Processing element ${idx}:`, elementInfo.tag, elementInfo.css_selector, elementInfo.bounding_box);
      let element = elementInfo.element;
      if (!element) {
        element = findElementByInfo(elementInfo, enableSmartSelectors);
        if (!element) {
          highlighterLogger.warn('[highlightElements] Could not find element for:', elementInfo);
          return;
        }
      }
      //if highlights requested for a specific doc, skip unrelated elements
      if (doc && element.ownerDocument !== doc) {
        highlighterLogger.debug("[highlightElements] Skipped element since it doesn't belong to document", doc);
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        highlighterLogger.warn('[highlightElements] Element has zero dimensions:', elementInfo);
        return;
      }
      // Create border highlight (red rectangle)
      // use ownerDocument to support iframes/frames
      const highlight = element.ownerDocument.createElement('div');
      highlight.style.cssText = `
        position: fixed;
        left: ${rect.x}px;
        top: ${rect.y}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        border: 1px solid rgb(255, 0, 0);
        transition: all 0.2s ease-in-out;
      `;
      // Create index label container - now positioned to the right and slightly up
      const labelContainer = element.ownerDocument.createElement('div');
      labelContainer.style.cssText = `
        position: absolute;
        right: -10px;     /* Offset to the right */
        top: -10px;       /* Offset upwards */
        padding: 4px;
        background-color: rgba(255, 255, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      const text = element.ownerDocument.createElement('span');
      text.style.cssText = `
        color: rgb(0, 0, 0, 0.8);
        font-family: 'Courier New', Courier, monospace;
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
      `;
      text.textContent = elementInfo.index.toString();
      labelContainer.appendChild(text);
      highlight.appendChild(labelContainer);      
      overlays[element.ownerDocument.documentURI].appendChild(highlight);
      
    });
  };

  // Initial highlight
  updateHighlights();

  if (handleScroll) {
    documents.forEach(doc => {
      // Update highlights on scroll and resize
      highlighterLogger.debug('registering scroll and resize handlers for document: ', doc);
      const scrollHandler = () => {
        requestAnimationFrame(() => updateHighlights(doc));
      }
      const resizeHandler = () => {
        updateHighlights(doc);
      }
      doc.addEventListener('scroll', scrollHandler, true);
      doc.addEventListener('resize', resizeHandler);
      // Store event handlers for cleanup
      overlays[doc.documentURI].scrollHandler = scrollHandler;
      overlays[doc.documentURI].resizeHandler = resizeHandler;
    });    
  }
}

// function unexecute() {
//   unhighlightElements();
// }

// Make it available globally for both Extension and Playwright
if (typeof window !== 'undefined') {
  function stripElementRefs(elementInfo) {
    if (!elementInfo) return null;
    const { element, ...rest } = elementInfo;
    return rest;
  }

  window.ProboLabs = window.ProboLabs || {};

  // --- Caching State ---
  window.ProboLabs.candidates = [];
  window.ProboLabs.actual = null;
  window.ProboLabs.matchingCandidate = null;

  // --- Methods ---
  /**
   * Find and cache candidate elements of a given type (e.g., 'CLICKABLE').
   * NOTE: This function is async and must be awaited from Playwright/Node.
   */
  window.ProboLabs.findAndCacheCandidateElements = async function(elementType) {
    //console.log('[ProboLabs] findAndCacheCandidateElements called with:', elementType);
    const found = await findElements(elementType);
    window.ProboLabs.candidates = found;
    // console.log('[ProboLabs] candidates set to:', found, 'type:', typeof found, 'isArray:', Array.isArray(found));
    return found.length;
  };

  window.ProboLabs.findAndCacheActualElement = function(iframeSelector, elementSelector, isHover=false, useSmartSelectors=false) {
    // console.log('[ProboLabs] findAndCacheActualElement called with:', cssSelector, iframeSelector);
    highlighterLogger.debug(`[ProboLabs] findAndCacheActualElement called with: iframeSelector=${iframeSelector}, elementSelector=${elementSelector}, useSmartSelectors=${useSmartSelectors}`);
    let el = findElementBySelectors(iframeSelector, elementSelector, useSmartSelectors);
    if(isHover) {
      const visibleElement = findClosestVisibleElement(el);
      if (visibleElement) {
        el = visibleElement;
      }
    }
    if (!el) {
      window.ProboLabs.actual = null;
      // console.log('[ProboLabs] actual set to null');
      return false;
    }
    window.ProboLabs.actual = getElementInfo(el, -1);
    // console.log('[ProboLabs] actual set to:', window.ProboLabs.actual);
    return true;
  };

  window.ProboLabs.findAndCacheMatchingCandidate = function() {
    // console.log('[ProboLabs] findAndCacheMatchingCandidate called');
    if (!window.ProboLabs.candidates.length || !window.ProboLabs.actual) {
      window.ProboLabs.matchingCandidate = null;
      // console.log('[ProboLabs] matchingCandidate set to null');
      return false;
    }
    window.ProboLabs.matchingCandidate = findMatchingCandidateElementInfo(window.ProboLabs.candidates, window.ProboLabs.actual);
    // console.log('[ProboLabs] matchingCandidate set to:', window.ProboLabs.matchingCandidate);
    return !!window.ProboLabs.matchingCandidate;
  };

  window.ProboLabs.highlightCachedElements = function(which) {
    let elements = [];
    if (which === 'candidates') elements = window.ProboLabs.candidates;
    if (which === 'actual' && window.ProboLabs.actual) elements = [window.ProboLabs.actual];
    if (which === 'matching' && window.ProboLabs.matchingCandidate) elements = [window.ProboLabs.matchingCandidate];
    highlighterLogger.debug(`[ProboLabs] highlightCachedElements ${which} with ${elements.length} elements`);
    highlightElements(elements);
  };

  window.ProboLabs.unhighlight = function() {
    // console.log('[ProboLabs] unhighlight called');
    unhighlightElements();
  };

  window.ProboLabs.reset = function() {
    highlighterLogger.debug('[ProboLabs] reset called');
    window.ProboLabs.candidates = [];
    window.ProboLabs.actual = null;
    window.ProboLabs.matchingCandidate = null;
    unhighlightElements();
  };

  window.ProboLabs.getCandidates = function() {
    // console.log('[ProboLabs] getCandidates called. candidates:', window.ProboLabs.candidates, 'type:', typeof window.ProboLabs.candidates, 'isArray:', Array.isArray(window.ProboLabs.candidates));
    const arr = Array.isArray(window.ProboLabs.candidates) ? window.ProboLabs.candidates : [];
    return arr.map(stripElementRefs);
  };
  window.ProboLabs.getActual = function() {
    return stripElementRefs(window.ProboLabs.actual);
  };
  window.ProboLabs.getMatchingCandidate = function() {
    return stripElementRefs(window.ProboLabs.matchingCandidate);
  };

  window.ProboLabs.setLoggerDebugLevel = function(debugLevel) {
    highlighterLogger.setLogLevel(debugLevel);
  };

  // Retain existing API for backward compatibility
  window.ProboLabs.ElementTag = ElementTag;
  window.ProboLabs.highlightElements = highlightElements;
  window.ProboLabs.unhighlightElements = unhighlightElements;
  window.ProboLabs.findElements = findElements;
  window.ProboLabs.getElementInfo = getElementInfo;
  window.ProboLabs.highlight = window.ProboLabs.highlight;
  window.ProboLabs.unhighlight = window.ProboLabs.unhighlight;

  // --- Utility Functions ---
  function findClosestVisibleElement(element) {
    let current = element;
    while (current) {
      const style = window.getComputedStyle(current);
      if (
        style &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        current.offsetWidth > 0 &&
        current.offsetHeight > 0
      ) {
        return current;
      }
      if (!current.parentElement || current === document.body) break;
      current = current.parentElement;
    }
    return null;
  }
}


